import Vue from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./services/store";
import vuetify from "./plugins/vuetify";
import ApiService from "@/services/api.service";
import OidcService from "@/services/oidc.service";
import generalFunctions from "./js/generalFunctions";
import "./assets/GoogleFont/font.css";
import axios from "axios";
import permissionDirective from "./directives/permission";

// don't forget to import CSS styles
import "tiptap-vuetify/dist/main.css";
const plugin = {
    install() {
        Vue.helpers = generalFunctions;
        Vue.prototype.$generalFunctions = generalFunctions;
    },
};

export const eventBus = new Vue(); // Listening for event to another component

axios.interceptors.request.use(
    (config) => {
        const token = window.localStorage.getItem("id_token");
        if (config.cancelToken) return config;

        if (token) config.headers["Authorization"] = "Bearer " + token;

        return { ...config, cancelToken: axios.CancelToken.source().token };
    },
    (err) => {
        throw err;
    }
);

// Helper function to extract error message from response
const extractErrorMessage = (response) => {
    if (!response) return "Errore sconosciuto";
    
    const data = response.data;
    if (!data) return `Errore HTTP ${response.status}`;
    
    // Try multiple possible error message fields
    return data.message || 
           data.Message || 
           data.error || 
           data.Error || 
           data.errorMessage || 
           data.detail || 
           data.title ||
           (typeof data === 'string' && data) ||
           `Errore HTTP ${response.status}`;
};

axios.interceptors.response.use(
    (response) => {
        if (
            response.config.method === "post" ||
            response.config.method === "put" ||
            response.config.method === "delete"
        ) {
            if (response.data.message) {
                store.commit("notifications/SET_SUCCESS", response.data.message);
                setTimeout(() => store.commit("notifications/SET_SUCCESS", ""), 5000);
            }
        }
        return response;
    },
    (error) => {
        const response = error.response;
        
        // Handle network errors
        if (!response) {
            const networkError = "Errore di connessione. Verificare la connessione internet.";
            store.commit("notifications/SET_ERROR", networkError);
            setTimeout(() => store.commit("notifications/SET_ERROR", ""), 5000);
            return Promise.reject(error);
        }
        
        const errorMessage = extractErrorMessage(response);
        
        switch (response.status) {
            case 400:
                // Bad Request - show detailed validation errors
                store.commit("notifications/SET_ERROR", errorMessage);
                setTimeout(() => store.commit("notifications/SET_ERROR", ""), 5000);
                break;
            case 401:
                // Unauthorized - redirect to login
                if (!window.location.href.includes("login")) {
                    router.push({ name: "login" });
                }
                break;
            case 403:
                // Forbidden
                store.commit("notifications/SET_ERROR", errorMessage || "Non autorizzato ad eseguire questa operazione");
                setTimeout(() => store.commit("notifications/SET_ERROR", ""), 5000);
                break;
            case 404:
                // Not Found
                store.commit("notifications/SET_ERROR", errorMessage || "Risorsa non trovata");
                setTimeout(() => store.commit("notifications/SET_ERROR", ""), 5000);
                break;
            case 422:
                // Unprocessable Entity - validation errors
                store.commit("notifications/SET_ERROR", errorMessage);
                setTimeout(() => store.commit("notifications/SET_ERROR", ""), 5000);
                break;
            case 500:
                // Internal Server Error
                store.commit("notifications/SET_ERROR", errorMessage || "Errore interno del server");
                setTimeout(() => store.commit("notifications/SET_ERROR", ""), 5000);
                break;
            case 502:
                // Bad Gateway
                store.commit("notifications/SET_ERROR", "Servizio temporaneamente non disponibile");
                setTimeout(() => store.commit("notifications/SET_ERROR", ""), 5000);
                break;
            case 503:
                // Service Unavailable
                store.commit("notifications/SET_ERROR", "Servizio temporaneamente non disponibile");
                setTimeout(() => store.commit("notifications/SET_ERROR", ""), 5000);
                break;
            default:
                // Other HTTP errors
                if (response.status >= 400) {
                    store.commit("notifications/SET_ERROR", errorMessage);
                    setTimeout(() => store.commit("notifications/SET_ERROR", ""), 5000);
                }
                break;
        }
        
        return Promise.reject(error);
    }
);

Vue.use(plugin);

Vue.prototype.window = window;
Vue.prototype.$logoSrc = process.env.VUE_APP_CLIENT
    ? require("@/assets/images/logo-" + process.env.VUE_APP_CLIENT + ".svg")
    : require("@/assets/images/logo.svg");

Vue.config.productionTip = false;

// Registra la direttiva v-permission globalmente
Vue.directive('permission', permissionDirective);

ApiService.init();
// Il mount dell'app aspetta che il controllo silenzioso OIDC sia finito (vedi
// oidc.service.js): cosi' nessun componente legge id_token prima che sia
// sincronizzato con un'eventuale sessione Keycloak gia' attiva.
const oidcReady = OidcService.init();

router.beforeEach((to, from, next) => {
    const publicRoutes = [
        "login",
        "forgot-password",
        "reset-password",
        "confirm-email",
        "auth-callback",
        "auth-silent",
    ];
    if (!publicRoutes.includes(to.name) && !localStorage.getItem("id_token")) {
        // Non tutti gli utenti hanno SSO: si atterra sempre sulla pagina di login,
        // che offre la scelta esplicita tra "Accedi con SSO Ciscra" (Login.vue,
        // solo se OidcService.enabled) e il form classico con le credenziali.
        // Nessun redirect automatico a Keycloak da qui.
        next({ name: "login" });
        return;
    }

    const requiredRole = to.matched
        .map((record) => record.meta?.requiresRole)
        .find((role) => !!role);

    if (requiredRole) {
        const currentUser = store?.state?.auth?.currentUser || null;
        const userRoles = currentUser?.roles || [];
        const hasRole = Array.isArray(userRoles) &&
            userRoles.some((r) => r.name && r.name.toLowerCase() === requiredRole.toLowerCase());
        if (!hasRole) {
            store.dispatch(
                "notifications/showError",
                "non hai il permesso per questa sezione"
            );

            if (from && from.name) {
                next(false);
                return;
            }

            next({ name: "dashboard" });
            return;
        }
    }

    const requiredLicenseFeature = to.matched
        .map((record) => record.meta?.requiredLicenseFeature)
        .find((feature) => !!feature);

    if (requiredLicenseFeature) {
        const currentUser = store?.state?.auth?.currentUser || null;
        const hasValidLicense = currentUser?.hasValidLicense === true ||
            (currentUser?.activeLicenseCount || 0) > 0;
        const licensedFeatures = Array.isArray(currentUser?.licensedFeatures)
            ? currentUser.licensedFeatures
                .filter((feature) => typeof feature === "string" && feature.trim() !== "")
                .map((feature) => feature.trim().toUpperCase())
            : [];
        const normalizedRequiredFeature = String(requiredLicenseFeature).trim().toUpperCase();

        const hasRequiredFeature = hasValidLicense && (
            !licensedFeatures.length ||
            licensedFeatures.includes("*") ||
            licensedFeatures.includes(normalizedRequiredFeature)
        );

        if (!hasRequiredFeature) {
            store.dispatch("notifications/showError", "non hai la licenza per questo");

            if (from && from.name) {
                next(false);
                return;
            }

            next({ name: "dashboard" });
            return;
        }
    }

    const requiredPermission = to.matched
        .map((record) => record.meta?.requiredPermission)
        .find((permission) => !!permission);

    if (requiredPermission) {
        const currentUser = store?.state?.auth?.currentUser || null;
        const userRoles = currentUser?.roles;
        const permissionValida =
            requiredPermission &&
            typeof requiredPermission.controller === "string" &&
            typeof requiredPermission.action === "string";

        // Anti lock-out: se i ruoli non sono (ancora) caricati — assenti, vuoti
        // o non array — NON si blocca la navigazione. Il backend resta l'autorità
        // e risponde comunque 403; la lista permessi può arrivare più tardi.
        const ruoliDisponibili =
            Array.isArray(userRoles) && userRoles.length > 0;

        if (permissionValida && ruoliDisponibili) {
            const ruoliConPermessi = userRoles.filter((role) =>
                Array.isArray(role?.permissions)
            );

            if (ruoliConPermessi.length > 0) {
                const hasPermission = generalFunctions.checkRolesPermissions(
                    ruoliConPermessi,
                    requiredPermission.controller,
                    requiredPermission.action
                );

                if (!hasPermission) {
                    store.dispatch(
                        "notifications/showError",
                        "non hai il permesso per questa sezione"
                    );

                    if (from && from.name) {
                        next(false);
                        return;
                    }

                    next({ name: "dashboard" });
                    return;
                }
            }
        }
    }

    next();
});

oidcReady.finally(() => {
    new Vue({
        router,
        store,
        vuetify,
        render: (h) => h(App),
    }).$mount("#app");
});
